/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { StatusCodes } from 'http-status-codes';

import type { AsyncAppThunk } from '../../../../ts/state';
import { doLogout } from '../../util/history';
import { extractErrorMessage } from '../../util/rest';
import * as types from './actionTypes';

const makeKey = ({
  message,
  status
}: {
  message: MessageType;
  status: ToastStatus | string;
}) => {
  // Message can be a react-intl message, use the ID here if it is.
  const messageKey = typeof message === 'string' ? message : message?.id;
  return btoa(unescape(encodeURIComponent(messageKey + status + Date.now())));
};

export const enum ToastStatus {
  ERROR = 'error',
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning'
}

type MessageType = string | { id: string; defaultMessage: string };

export type AddToastMessage = {
  message: MessageType;
  status: ToastStatus | string;
  transactionId?: string;
  timeout?: number;
  docUrl?: string;
  title?: string;
  autoremove?: boolean;
  values?: { [key: string]: string };
  override?: boolean;
  unique?: boolean;
};
const autoremoval = {};

export const accessDenied =
  (transactionId: string): AsyncAppThunk =>
  async dispatch => {
    dispatch({
      type: types.ACCESS_DENIED,
      message: {
        transactionId
      }
    });
  };

export const addMessage =
  ({
    docUrl, // a link of the document
    title, // a specific string you want to show in the notification
    message, // string or react-intl message
    status,
    values = {}, // values for substitution in react-intl messages.
    transactionId = '',
    timeout = 5000,
    autoremove = status === ToastStatus.SUCCESS || status === ToastStatus.INFO,
    override = false,
    unique = true
  }: AddToastMessage): AsyncAppThunk<Promise<string>> =>
  async (dispatch, getState) => {
    const key = makeKey({ message, status });
    if (unique) {
      const messages = getState()?.messages?.messages || [];
      const dupes = messages.filter(
        m => m.message === message && m.status === status
      );
      dupes.forEach(m => {
        dispatch(removeMessage(m.key));
        delete autoremoval[m.key];
      });
    }
    dispatch({
      type: override ? types.OVERRIDE_MESSAGE : types.ADD_MESSAGE,
      message: {
        docUrl,
        title,
        message,
        values,
        status,
        key,
        transactionId
      }
    });
    if (autoremove) {
      autoremoval[key] = setTimeout(() => {
        dispatch(removeMessage(key));
        delete autoremoval[key];
      }, timeout);
    }
    return key;
  };

export const removeMessage =
  (key: string): AsyncAppThunk =>
  async dispatch => {
    dispatch({
      type: types.REMOVE_MESSAGE,
      key
    });
  };

export const removeAllErrors =
  (): AsyncAppThunk => async (dispatch, getState) => {
    const messages = getState()?.messages?.messages || [];
    const errors = messages.filter(m => m.status !== ToastStatus.ERROR);
    errors.forEach(m => {
      delete autoremoval[m.key];
    });
    dispatch({
      type: types.OVERRIDE_MESSAGE,
      message: errors
    });
  };

export const addApiErrorMessage =
  (error): AsyncAppThunk =>
  async dispatch => {
    const DEFAULT_ERROR =
      'There was an uncaught error. Please refresh the page and try again.';
    if (error.status && error.statusText) {
      if (error.status === StatusCodes.UNAUTHORIZED) {
        dispatch(
          addMessage({
            message:
              'You are no longer logged in. Re-directing you to the login page.',
            status: ToastStatus.ERROR,
            override: true,
            timeout: 6000,
            transactionId: error.transactionId
          })
        );

        doLogout();
      } else if (parseInt(error.status) === StatusCodes.FORBIDDEN) {
        dispatch(accessDenied(error.transactionId));
      } else {
        let errorMessage = error.message ? `${error.message} ` : '';
        if (error.data && error.data.message) {
          errorMessage += error.data.message;
        } else if (error.data && error.data.error) {
          errorMessage += error.data.error;
        } else if (error.statusText) {
          errorMessage += error.statusText;
        } else {
          errorMessage += DEFAULT_ERROR;
        }

        dispatch(
          addMessage({
            message: errorMessage,
            status: ToastStatus.ERROR,
            timeout: 30000,
            transactionId: error.transactionId
          })
        );
      }
    } else {
      dispatch(
        addMessage({
          message: error && error.message ? error.message : DEFAULT_ERROR,
          status: ToastStatus.ERROR,
          transactionId: error.transactionId
        })
      );
    }
  };

export const addErrorMessage =
  (error: any): AsyncAppThunk<Promise<string>> =>
  async dispatch =>
    dispatch(
      addMessage({
        message: extractErrorMessage(error),
        values: error.values, // pass values to be displayed in error message.
        status: ToastStatus.ERROR,
        transactionId: error.transactionId
      })
    );
